import { type AuditEcmsForm } from '#packages/data-collection/models/audit-ecms-form';
import { ECM_DEFINITIONS } from '#apps/client-web/features/audit-form-ecms/constants';

import { type ExcelFieldMapping, type ExcelSheetMapping } from '../types/excel-mapping';

/**
 * Maps ECM form answers to Excel named ranges in the "4. ECMs" sheet.
 *
 * For every (ecm, criterion) pair declared in `ECM_DEFINITIONS`, two named
 * ranges exist in the template:
 *   - `{ecm.id}_{criterion.id}`         — Yes / No answer cell
 *   - `{ecm.id}_{criterion.id}_notes`   — free-text notes cell
 *
 * The answer cell is always written: `true` → "Yes", anything else → "No".
 * The notes cell is written only when a note is present.
 */
const findCriterion = (
  form: AuditEcmsForm,
  ecmId: string,
  criterionId: string,
) =>
  form.ecms
    .find((entry) => entry.id === ecmId)
    ?.criteria.find((c) => c.id === criterionId);

const buildEcmFields = (): ExcelFieldMapping<AuditEcmsForm>[] => {
  const fields: ExcelFieldMapping<AuditEcmsForm>[] = [];

  for (const ecm of ECM_DEFINITIONS) {
    for (const criterion of ecm.criteria) {
      const rangeBase = `${ecm.id}_${criterion.id}`;

      fields.push({
        rangeName: rangeBase,
        getValue: (form) =>
          findCriterion(form, ecm.id, criterion.id)?.answer === true,
      });

      fields.push({
        rangeName: `${rangeBase}_notes`,
        getValue: (form) => findCriterion(form, ecm.id, criterion.id)?.notes,
      });
    }
  }

  return fields;
};

export const ecmsSheetMapping: ExcelSheetMapping<AuditEcmsForm> = {
  label: 'ECMs',
  fields: buildEcmFields(),
};
